import type { LensCardData } from '~/types/leLens';

export type MarketingChoices = {
  options: Record<string, Array<string>>;
  /** Values present in the selected dates per filter; larger than the list when the source capped it. */
  totals: Record<string, number>;
  availability?: Record<string, Record<string, boolean>>;
  hasMore?: Record<string, boolean>;
  cursors?: Record<string, string>;
};

/**
 * Dropdown values from the batched `choices` card (svc-reporting reads the daily dbt value tables).
 * Any missing, failed, truncated or malformed batch yields no options, so dropdowns fall back to All
 * while the report itself still renders.
 */
export function marketingChoices(cards: ReadonlyArray<LensCardData> | undefined): MarketingChoices {
  const empty: MarketingChoices = { options: {}, totals: {} };
  const card = cards?.find((candidate) => ['choices', 'sales_choices'].includes(candidate.cardId));
  if (!card || !['ready', 'empty'].includes(card.status) || card.truncated) return empty;
  const options: Record<string, Array<string>> = {},
    totals: Record<string, number> = {};
  const availability: Record<string, Record<string, boolean>> = {};
  const hasMore: Record<string, boolean> = {};
  const cursors: Record<string, string> = {};
  try {
    for (const batch of card.rows ?? []) {
      if (typeof batch.payload !== 'string') return empty;
      const records: unknown = JSON.parse(batch.payload);
      if (!Array.isArray(records) || records.length !== Number(batch.record_count)) return empty;
      for (const record of records) {
        const entry = (record ?? {}) as Record<string, unknown>;
        const { kind, value, kind_total: total } = entry;
        if (typeof kind !== 'string' || typeof value !== 'string') return empty;
        if (entry.listed !== false) (options[kind] ??= []).push(value);
        if (typeof entry.available === 'boolean') (availability[kind] ??= {})[value] = entry.available;
        if (entry.has_more === true) hasMore[kind] = true;
        if (typeof entry.page_cursor === 'string') cursors[kind] = entry.page_cursor;
        if (typeof total === 'number') totals[kind] = total;
      }
    }
  } catch {
    return empty;
  }
  for (const values of Object.values(options)) values.sort((a, b) => a.localeCompare(b));
  return { options, totals, availability, hasMore, cursors };
}
